import { useMemo } from 'react';

import { ExtensionKey } from 'flow/types/EntityKeys';
import { useAppSelector } from 'flow/hooks/useRedux';

import { prettyFormatE164Number } from 'flow/utils/call-utils';
import {
    selectContactEmail,
    selectContactFirstName,
    selectContactFixedNumber,
    selectContactLastName,
    selectContactMobileNumber,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    extensionKey: ExtensionKey;
    fallbackToOtherContactInfo?: boolean;
}
const ExtensionName = ({
    extensionKey,
    fallbackToOtherContactInfo = true,
}: Props) => {
    const { i18next } = useAppContext();
    const firstName = useAppSelector((state) =>
        selectContactFirstName(state, extensionKey),
    );
    const lastName = useAppSelector((state) =>
        selectContactLastName(state, extensionKey),
    );

    // Composed from the two halves rather than read as one string: a contact
    // holding neither name has to come out empty, or the fallbacks below never
    // run for it.
    const contactName = useMemo(
        () => `${firstName || ''} ${lastName || ''}`.trim(),
        [firstName, lastName],
    );

    if (contactName) return contactName;
    if (fallbackToOtherContactInfo)
        return <ExtensionFallbackInfo extensionKey={extensionKey} />;
    return i18next.t('Contact.Unknown');
};

export default ExtensionName;

interface FallbackInfoProps {
    extensionKey: ExtensionKey;
}

const ExtensionFallbackInfo = ({ extensionKey }: FallbackInfoProps) => {
    const { i18next } = useAppContext();

    const contactFixedNumber = useAppSelector((state) =>
        selectContactFixedNumber(state, extensionKey),
    );
    const contactMobileNumber = useAppSelector((state) =>
        selectContactMobileNumber(state, extensionKey),
    );
    const userCountryCode = useAppSelector(selectUserCountryCode);
    const contactEmail = useAppSelector((state) =>
        selectContactEmail(state, extensionKey),
    );

    return (
        prettyFormatE164Number(
            contactFixedNumber || contactMobileNumber,
            userCountryCode,
        ) ||
        contactEmail ||
        i18next.t('Contact.Unknown')
    );
};

ExtensionFallbackInfo.displayName = 'ExtensionFallbackInfo';
